import { useId, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  apiErrorCodeSchema,
  WHATSAPP_STOP_LINE_MAX,
  WHATSAPP_TEMPLATE_BODY_MAX,
  WHATSAPP_TEMPLATE_NAME_MAX,
  type WhatsAppTemplateKeyCode,
  type WhatsAppTemplateResubmission,
  type WhatsAppTemplatesResponse,
  type WhatsAppTemplateView,
} from "@gioia365/contracts";
import {
  canonicalTemplateBody,
  renderTemplate,
  TEMPLATE_VARIABLES,
  templateVariableAtEdge,
  WHATSAPP_SUGGESTED_AFTER,
  withStopLine,
  type TemplateVariable,
} from "@gioia365/domain/whatsapp";
import type { MessageKey } from "@gioia365/i18n";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { useTab } from "../../api/queries";
import { Button } from "../../design/components/Button";
import { Chip } from "../../design/components/Chip";
import { Reason } from "../../design/components/Reason";
import { SettingsLayout } from "../../design/components/SettingsLayout";
import { StatusPill } from "../../design/components/StatusPill";
import { TextArea } from "../../design/components/TextArea";
import { TextField } from "../../design/components/TextField";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useT, type Translate } from "../../i18n/locale";
import { outcomeLabel } from "../../leads/lead-display";
import { useAccess } from "../../session/access";
import type { TabContext } from "../../session/tab-context";
import { whatsAppTemplatesQuery } from "../../whatsapp/api";
import { templateFailureReasonKey } from "../../whatsapp/meta-texts";
import { useSubmitWhatsAppTemplate, useUpdateWhatsAppStopLine, useUpdateWhatsAppTemplate, whatsAppChannelsQuery } from "../../whatsapp/settings-api";
import { LeaveDialog, useLeaveGuard, type Feedback } from "./draft-dialogs";
import "../../whatsapp/whatsapp.css";
import "./whatsapp-settings.css";

// Impostazioni → «Modelli WhatsApp» (piano M6, R1, Task 5a; D5, D25; questioni 395 e 401; mock-up `modelli-whatsapp.html` e
// `mockup.js`, `initTemplates`). I sei modelli dell'azienda (`GET /api/whatsapp/templates`, la stessa lettura della finestra del
// WhatsApp): li legge ogni persona attiva, li cambia chi ha «Modelli WhatsApp» (`canEdit` del server). Per ognuno:
// - il nome (un campo per chi li cambia, il titolo per gli altri) e dove si propone dopo la chiamata, da `WHATSAPP_SUGGESTED_AFTER`
//   del dominio (in M6 i passi dopo la chiamata sono fissi, questione 385); gli altri si mandano da «Invia WhatsApp»;
// - il testo, con «Inserisci nel testo», che mette una variabile dov'è il cursore (o al posto della parte scelta) e lascia il
//   cursore subito dopo;
// - «Come arriva a Giulia Ferri»: l'esempio del mock-up riempito nel browser con `renderTemplate`, con in fondo la riga «scrivi
//   STOP» dell'azienda (`withStopLine`), come arriva davvero (D25);
// - «Salva», con nome e testo e la versione su cui le modifiche sono state pensate (quella letta alla prima modifica, anche se intanto
//   la pagina si rilegge, per esempio al ritorno sulla scheda del browser): un modello cambiato nel frattempo da un altro risponde
//   `409 version_conflict`, si rilegge, le modifiche restano sopra e la scheda lo dice; il salvataggio dopo porta la versione nuova.
//   Un testo che con la riga «scrivi STOP» in fondo supera la lunghezza di un WhatsApp non si salva, e «Inserisci nel testo» non lo
//   allunga oltre (dal Task 5b lo controlla anche il server, con `422 validation_failed`).
// Sopra i modelli la riga «scrivi STOP» dell'azienda (questione 395): al massimo 60 caratteri, con la sua versione allo stesso modo,
// e non si toglie; una riga che farebbe superare la lunghezza a un modello salvato non si salva.
// I modelli sono i sei di partenza: non se ne creano e non se ne cancellano (questione 401). Una modifica non salvata ferma l'uscita
// dalla pagina, come le bozze delle altre Impostazioni.
// Il WhatsApp ufficiale di Meta (piano M6, R2, Task 8b; D25, E1; ruling B4, A4c; questioni 409 e 411; mock-up `initTemplates`,
// `metaLine`): soltanto quando l'azienda usa il WhatsApp ufficiale e Gioia365 è collegata a Meta (`GET /api/settings/whatsapp-
// channels`, `meta` e `metaSignup.available`), altrimenti la pagina resta quella di R1. Allora:
// - sotto le parole che si riempiono, come parte il messaggio con ogni WhatsApp (il mock-up, senza Agile fino a R9);
// - per ogni modello (`TemplateMetaLine`) lo stato presso Meta («Meta: da inviare / in revisione / approvato / rifiutato / sospeso /
//   spento», con il motivo), la categoria (quella decisa da Meta, detta quando è diversa da quella proposta) e l'ultimo invio non
//   riuscito (`metaError`); per chi cambia i modelli «Invia a Meta per l'approvazione» (`POST …/submit`, il testo salvato), che manca
//   per un modello già in revisione o approvato ed è spento, con il perché, senza il collegamento dell'azienda presso Meta, con un
//   testo che comincia o finisce con una variabile (questione 411: «aggiungi una parola o un punto», detto prima dell'invio) o con un
//   testo non salvato. Un testo cambiato di un modello su Meta lo dice prima del salvataggio («Un testo cambiato torna in
//   revisione»), e dopo l'avviso dice se è ripartito per Meta (`metaResubmission`) o perché no;
// - la riga «scrivi STOP», piè di pagina dei modelli su Meta: cambiata con modelli già su Meta lo dice prima di salvare («nel
//   frattempo si mandano a mano», E1), e dopo quali sono tornati in revisione e quali restano da mandare (`metaResubmissions`).
// Un rifiuto del server si dice nella scheda del modello con le parole dei cataloghi, mai con il messaggio del server o il codice di
// Meta.

/** Le modifiche non salvate di un modello e la versione su cui sono state pensate (quella letta alla prima modifica). */
type TemplateEdits = { name?: string; body?: string; baseVersion?: number };

/** La riga «scrivi STOP» non salvata e la versione su cui è stata pensata. */
type StopDraft = { value: string; baseVersion: number };

/** La versione di adesso che un `409 version_conflict` porta (`details.version`), se c'è. */
function conflictVersion(error: ApiRequestError): number | null {
  const version = error.details?.version;
  return typeof version === "number" ? version : null;
}

/** Quanti caratteri ha il WhatsApp di questo testo, con la riga «scrivi STOP» in fondo (le variabili come sono scritte). */
function sendableLength(body: string, stopLine: string): number {
  const text = body.trim();
  if (text === "") return 0;
  return (stopLine.trim() === "" ? text : withStopLine(text, stopLine)).length;
}

/** La variabile come si scrive nel testo: fra graffe, `{giorno e ora}` con gli spazi. */
function variableToken(variable: TemplateVariable): string {
  return `{${variable}}`;
}

/**
 * Il significato di ogni variabile nei cataloghi (`WA_VARIABLES` del mock-up, con i nomi di WA-13 dal 17/10/2026). Soltanto i nomi di
 * adesso: quelli di prima (`{salone}`, `{azienda}`) si riempiono ancora nell'anteprima, ma non si offrono.
 */
const VARIABLE_MEANINGS: Readonly<Record<TemplateVariable, MessageKey>> = {
  nome: "whatsappTemplatesSettings.variables.nome",
  AziendaTarget: "whatsappTemplatesSettings.variables.aziendaTarget",
  commerciale: "whatsappTemplatesSettings.variables.commerciale",
  NostraAzienda: "whatsappTemplatesSettings.variables.nostraAzienda",
  "giorno e ora": "whatsappTemplatesSettings.variables.dayAndTime",
};

/**
 * Il WhatsApp ufficiale nella pagina, quando l'azienda lo usa e Gioia365 è collegata a Meta: se l'azienda è già collegata a Meta
 * (`connected`; senza, «Invia a Meta per l'approvazione» è spento con il motivo della pagina, `notConnectedId`).
 */
type MetaPage = { connected: boolean; notConnectedId: string };

type MetaStatus = NonNullable<WhatsAppTemplateView["metaStatus"]>;

/** Come si mostra lo stato presso Meta: testo, tono e icona insieme, mai il solo colore (`metaLine` del mock-up). */
const META_LOOK: Readonly<Record<MetaStatus | "none", { label: MessageKey; tone: "neutral" | "warning" | "success" | "danger"; icon: "clock" | "check" | "alert" | "minus" }>> = {
  none: { label: "whatsappTemplatesSettings.meta.status.none", tone: "neutral", icon: "clock" },
  pending: { label: "whatsappTemplatesSettings.meta.status.pending", tone: "warning", icon: "clock" },
  approved: { label: "whatsappTemplatesSettings.meta.status.approved", tone: "success", icon: "check" },
  rejected: { label: "whatsappTemplatesSettings.meta.status.rejected", tone: "danger", icon: "alert" },
  paused: { label: "whatsappTemplatesSettings.meta.status.paused", tone: "warning", icon: "alert" },
  disabled: { label: "whatsappTemplatesSettings.meta.status.disabled", tone: "danger", icon: "minus" },
};

const CATEGORY_LABEL: Readonly<Record<WhatsAppTemplateView["category"], MessageKey>> = {
  utility: "whatsappTemplatesSettings.meta.category.utility",
  marketing: "whatsappTemplatesSettings.meta.category.marketing",
};

/** I motivi di un rifiuto che Meta documenta (`rejected_reason`), in parole; uno che non si conosce, o nessuno, vale «non l'ha detto». */
const REJECTED_REASON: Readonly<Record<string, MessageKey>> = {
  ABUSIVE_CONTENT: "whatsappTemplatesSettings.meta.rejectedReason.abusiveContent",
  INCORRECT_CATEGORY: "whatsappTemplatesSettings.meta.rejectedReason.incorrectCategory",
  INVALID_FORMAT: "whatsappTemplatesSettings.meta.rejectedReason.invalidFormat",
  PROMOTIONAL: "whatsappTemplatesSettings.meta.rejectedReason.promotional",
  SCAM: "whatsappTemplatesSettings.meta.rejectedReason.scam",
  TAG_CONTENT_MISMATCH: "whatsappTemplatesSettings.meta.rejectedReason.tagContentMismatch",
};

function rejectedReasonText(t: Translate, reason: string | null): string {
  const key = reason === null ? undefined : REJECTED_REASON[reason.trim().toUpperCase()];
  return t(key ?? "whatsappTemplatesSettings.meta.rejectedReason.unknown");
}

/**
 * Perché un modello non è ripartito per Meta dopo un salvataggio: il motivo di Meta (`failed`) o il rifiuto che l'invio avrebbe
 * avuto (`skipped`, un codice dell'API), con le parole della pagina per l'azienda non ancora collegata a Meta.
 */
function resubmissionReason(t: Translate, resubmission: WhatsAppTemplateResubmission): string {
  if (resubmission.outcome === "failed") return t(templateFailureReasonKey(resubmission.code));
  if (resubmission.code === "whatsapp_meta_window_required") return t("whatsappTemplatesSettings.meta.notConnected");
  const code = apiErrorCodeSchema.safeParse(resubmission.code);
  return code.success ? t(`errors.${code.data}`) : t("errors.unexpected");
}

/**
 * Un nuovo invio da dire dopo un salvataggio: non partito o non accettato. `version_conflict` no: il testo è cambiato di nuovo nel
 * frattempo, e lo rimanda quel salvataggio.
 */
const notResubmitted = (resubmission: WhatsAppTemplateResubmission) => resubmission.outcome !== "submitted" && resubmission.code !== "version_conflict";

/** «Si propone dopo «Nessuna risposta». Sempre disponibile da «Invia WhatsApp».», o dove si manda un modello senza percorso. */
function suggestedText(t: Translate, key: WhatsAppTemplateKeyCode): string {
  const outcomes = Object.entries(WHATSAPP_SUGGESTED_AFTER)
    .filter(([, template]) => template === key)
    .map(([outcome]) => t("whatsappTemplatesSettings.template.after", { outcome: outcomeLabel(t, outcome) }));
  return outcomes.length > 0 ? t("whatsappTemplatesSettings.template.suggested", { outcomes: outcomes.join(", ") }) : t("whatsappTemplatesSettings.template.notSuggested");
}

/** L'anteprima: l'esempio riempito, senza spazi ai bordi, e la riga dell'azienda in fondo (una volta sola); vuota senza testo. */
function previewText(body: string, values: Partial<Record<TemplateVariable, string>>, stopLine: string): string {
  const text = renderTemplate(body, values).text.trim();
  if (text === "") return "";
  return stopLine.trim() === "" ? text : withStopLine(text, stopLine);
}

export function WhatsAppTemplatesPage() {
  return (
    <SettingsLayout section="whatsappTemplates">
      <WhatsAppTemplatesSection />
    </SettingsLayout>
  );
}

function WhatsAppTemplatesSection() {
  const t = useT();
  const tab = useTab();
  const notConnectedId = useId();
  // La pagina rilegge sempre i modelli quando si apre: la finestra del WhatsApp li tiene un minuto, qui serve la versione di adesso.
  const templates = useQuery({ ...whatsAppTemplatesQuery(tab), staleTime: 0 });
  // I WhatsApp che usa l'azienda: lo stato dei modelli presso Meta si mostra soltanto con il WhatsApp ufficiale acceso e collegato.
  const channels = useQuery(whatsAppChannelsQuery(tab));
  const data = templates.data;
  const company = channels.data;
  const meta: MetaPage | null =
    company?.meta === true && company.metaSignup.available ? { connected: company.metaSignup.connection?.state === "connected", notConnectedId } : null;

  let body: ReactNode;
  if (data) {
    body = <TemplatesEditor tab={tab} data={data} meta={meta} />;
  } else if (templates.isError) {
    body = (
      <div className="g-wa-problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, templates.error)}
        </Reason>
        <Button onClick={() => void templates.refetch()} disabled={templates.isFetching} aria-busy={templates.isFetching ? "true" : undefined}>
          {t("common.retry")}
        </Button>
      </div>
    );
  } else {
    body = <p className="g-wa-muted">{t("common.loading")}</p>;
  }

  return (
    <>
      <p className="g-wa-muted">{t("whatsappTemplatesSettings.intro")}</p>
      {data && !data.canEdit ? <Reason>{t("whatsappTemplatesSettings.readOnly")}</Reason> : null}
      {channels.isError ? (
        <div className="g-wa-problem">
          <Reason tone="warning" role="alert">
            {t("whatsappTemplatesSettings.meta.channelsFailed")}
          </Reason>
          <Button onClick={() => void channels.refetch()} disabled={channels.isFetching} aria-busy={channels.isFetching ? "true" : undefined}>
            {t("common.retry")}
          </Button>
        </div>
      ) : null}
      {meta && !meta.connected && data?.canEdit ? <Reason id={notConnectedId}>{t("whatsappTemplatesSettings.meta.notConnected")}</Reason> : null}
      {body}
    </>
  );
}

type EditorProps = { tab: TabContext; data: WhatsAppTemplatesResponse; meta: MetaPage | null };

function TemplatesEditor({ tab, data, meta }: EditorProps) {
  const t = useT();
  const access = useAccess();
  const varsId = useId();
  // Le modifiche non salvate, per modello, e quella della riga: sopra quello che il server ha adesso.
  const [edits, setEdits] = useState<Partial<Record<WhatsAppTemplateKeyCode, TemplateEdits>>>({});
  const [stopDraft, setStopDraft] = useState<StopDraft | null>(null);
  const canEdit = data.canEdit;
  const values: Partial<Record<TemplateVariable, string>> = {
    nome: t("whatsappTemplatesSettings.sample.nome"),
    AziendaTarget: t("whatsappTemplatesSettings.sample.aziendaTarget"),
    commerciale: t("whatsappTemplatesSettings.sample.commerciale"),
    NostraAzienda: access.context.organization.name,
    "giorno e ora": t("whatsappTemplatesSettings.sample.dayAndTime"),
  };
  const dirty =
    canEdit &&
    ((stopDraft !== null && stopDraft.value !== data.stopLine) ||
      data.templates.some((template) => {
        const edit = edits[template.key];
        return edit !== undefined && ((edit.name ?? template.name) !== template.name || (edit.body ?? template.body) !== template.body);
      }));
  const blocker = useLeaveGuard(dirty);

  /** Una modifica di un modello; la prima fissa la versione mostrata, su cui la modifica è pensata. `null` le toglie tutte. */
  function editTemplate(template: WhatsAppTemplateView, change: TemplateEdits | null) {
    const { key, version } = template;
    setEdits((current) => {
      if (change === null) {
        const rest = { ...current };
        delete rest[key];
        return rest;
      }
      const before = current[key];
      return { ...current, [key]: { ...before, ...change, baseVersion: change.baseVersion ?? before?.baseVersion ?? version } };
    });
  }

  return (
    <>
      <section className="g-wa-section g-wa-variables" aria-labelledby={varsId}>
        <h2 id={varsId}>{t("whatsappTemplatesSettings.variablesTitle")}</h2>
        <ul>
          {TEMPLATE_VARIABLES.map((variable) => (
            <li key={variable}>
              <Chip>{variableToken(variable)}</Chip> {t(VARIABLE_MEANINGS[variable])}
            </li>
          ))}
        </ul>
        {meta ? <p className="g-wa-muted">{t("whatsappTemplatesSettings.meta.howItLeaves")}</p> : null}
      </section>
      <StopLineSection tab={tab} data={data} draft={stopDraft} onDraft={setStopDraft} meta={meta} />
      <div className="g-wa-templates">
        {data.templates.map((template) => (
          <TemplateCard
            key={template.key}
            tab={tab}
            template={template}
            canEdit={canEdit}
            edits={edits[template.key] ?? {}}
            onEdit={(change) => editTemplate(template, change)}
            values={values}
            stopLine={data.stopLine}
            meta={meta}
          />
        ))}
      </div>
      {blocker.status === "blocked" ? (
        <LeaveDialog
          onStay={() => blocker.reset()}
          onLeave={() => {
            setEdits({});
            setStopDraft(null);
            blocker.proceed();
          }}
        />
      ) : null}
    </>
  );
}

type StopLineProps = {
  tab: TabContext;
  data: WhatsAppTemplatesResponse;
  draft: StopDraft | null;
  onDraft: (draft: StopDraft | null) => void;
  meta: MetaPage | null;
};

/**
 * La riga «scrivi STOP» dell'azienda (questione 395): si cambia con la sua versione, non si toglie. Con il WhatsApp ufficiale è il piè
 * di pagina dei modelli su Meta (D25): una riga cambiata con modelli già su Meta lo dice prima di salvare, e dopo dice quali sono
 * tornati in revisione e quali restano da mandare.
 */
function StopLineSection({ tab, data, draft, onDraft, meta }: StopLineProps) {
  const t = useT();
  const toast = useToast();
  const ids = useId();
  const save = useUpdateWhatsAppStopLine(tab);
  const [error, setError] = useState<Feedback | null>(null);
  const [problem, setProblem] = useState<Feedback | null>(null);
  // I modelli che dopo l'ultimo salvataggio non sono ripartiti per Meta: restano da mandare.
  const [leftBehind, setLeftBehind] = useState<WhatsAppTemplateKeyCode[]>([]);
  const fieldRef = useRef<HTMLInputElement>(null);
  const value = draft?.value ?? data.stopLine;
  const busy = save.isPending;
  const warnMeta = meta !== null && value.trim() !== data.stopLine && data.templates.some((template) => template.metaStatus !== null);

  /** I nomi dei modelli, fra «», separati da una virgola. */
  const namesOf = (tr: Translate, keys: readonly WhatsAppTemplateKeyCode[]) =>
    keys
      .map((key) => data.templates.find((template) => template.key === key)?.name ?? key)
      .map((name) => tr("whatsappTemplatesSettings.meta.quoted", { name }))
      .join(", ");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const stopLine = value.trim();
    if (stopLine === "") {
      setError({ text: (tr) => tr("whatsappTemplatesSettings.stopLine.empty") });
      fieldRef.current?.focus();
      return;
    }
    // Ogni modello salvato, con questa riga in fondo, deve restare un WhatsApp che si può mandare.
    const over = data.templates.find((template) => sendableLength(template.body, stopLine) > WHATSAPP_TEMPLATE_BODY_MAX);
    if (over) {
      const name = over.name;
      setError({ text: (tr) => tr("whatsappTemplatesSettings.stopLine.tooLong", { name, max: WHATSAPP_TEMPLATE_BODY_MAX }) });
      fieldRef.current?.focus();
      return;
    }
    setError(null);
    setProblem(null);
    setLeftBehind([]);
    try {
      const saved = await save.mutateAsync({ stopLine, expectedVersion: draft?.baseVersion ?? data.stopLineVersion });
      onDraft(null);
      // Senza il WhatsApp ufficiale nella pagina, la riga è soltanto quella dei WhatsApp a mano (R1).
      const resubmitted = meta ? saved.metaResubmissions.filter((resubmission) => resubmission.outcome === "submitted").map((resubmission) => resubmission.key) : [];
      if (resubmitted.length > 0) toast.show(t("whatsappTemplatesSettings.stopLine.savedInReview", { names: namesOf(t, resubmitted) }), "success");
      else toast.show(t("whatsappTemplatesSettings.stopLine.saved"), "success");
      if (meta) setLeftBehind(saved.metaResubmissions.filter(notResubmitted).map((resubmission) => resubmission.key));
    } catch (failure) {
      // La riga si rilegge: la modifica resta sopra quella salvata, e il salvataggio dopo porta la versione di adesso.
      if (failure instanceof ApiRequestError && failure.code === "version_conflict") {
        onDraft({ value, baseVersion: conflictVersion(failure) ?? data.stopLineVersion });
        setProblem({ text: (tr) => tr("whatsappTemplatesSettings.stopLine.conflict") });
      } else {
        setProblem({ text: (tr) => messageForError(tr, failure) });
      }
    }
  }

  return (
    <section className="g-wa-section" aria-labelledby={`${ids}-title`}>
      <h2 id={`${ids}-title`}>{t("whatsappTemplatesSettings.stopLine.title")}</h2>
      <p className="g-wa-muted">{t("whatsappTemplatesSettings.stopLine.text")}</p>
      {data.canEdit ? (
        <form className="g-wa-stop" onSubmit={(event) => void submit(event)} aria-busy={busy} noValidate>
          <TextField
            ref={fieldRef}
            id={`${ids}-line`}
            label={t("whatsappTemplatesSettings.stopLine.label")}
            hint={t("whatsappTemplatesSettings.stopLine.hint", { max: WHATSAPP_STOP_LINE_MAX })}
            error={error?.text(t)}
            value={value}
            maxLength={WHATSAPP_STOP_LINE_MAX}
            autoComplete="off"
            disabled={busy}
            onChange={(event) => {
              onDraft({ value: event.target.value, baseVersion: draft?.baseVersion ?? data.stopLineVersion });
              setError(null);
            }}
          />
          <Button variant="primary" type="submit" disabled={busy} aria-busy={busy ? "true" : undefined} aria-describedby={warnMeta && data.canEdit ? `${ids}-meta` : undefined}>
            {t("whatsappTemplatesSettings.stopLine.save")}
          </Button>
        </form>
      ) : (
        <p className="g-wa-template__body">{data.stopLine}</p>
      )}
      {warnMeta && data.canEdit ? (
        <Reason tone="warning" id={`${ids}-meta`}>
          {t("whatsappTemplatesSettings.stopLine.metaWarning")}
        </Reason>
      ) : null}
      {problem ? (
        <Reason tone="warning" role="alert">
          {problem.text(t)}
        </Reason>
      ) : null}
      {leftBehind.length > 0 ? (
        <Reason tone="warning" role="alert">
          {t("whatsappTemplatesSettings.stopLine.notResubmitted", { names: namesOf(t, leftBehind) })}
        </Reason>
      ) : null}
    </section>
  );
}

type CardProps = {
  tab: TabContext;
  template: WhatsAppTemplateView;
  canEdit: boolean;
  edits: TemplateEdits;
  /** Una modifica del nome, del testo o della versione di partenza; `null` le toglie tutte (dopo il salvataggio). */
  onEdit: (change: TemplateEdits | null) => void;
  values: Partial<Record<TemplateVariable, string>>;
  stopLine: string;
  meta: MetaPage | null;
};

/**
 * Un modello: il nome, dove si propone, lo stato presso Meta (con il WhatsApp ufficiale), il testo con «Inserisci nel testo»,
 * l'anteprima e «Salva».
 */
function TemplateCard({ tab, template, canEdit, edits, onEdit, values, stopLine, meta }: CardProps) {
  const t = useT();
  const toast = useToast();
  const ids = useId();
  const update = useUpdateWhatsAppTemplate(tab);
  const submit = useSubmitWhatsAppTemplate(tab);
  const nameRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const [nameError, setNameError] = useState<Feedback | null>(null);
  const [textError, setTextError] = useState<Feedback | null>(null);
  const [problem, setProblem] = useState<Feedback | null>(null);
  // Dove rimettere il cursore dopo «Inserisci nel testo», quando il testo nuovo è nella pagina.
  const [caret, setCaret] = useState<number | null>(null);
  const name = canEdit ? (edits.name ?? template.name) : template.name;
  const body = canEdit ? (edits.body ?? template.body) : template.body;
  // Mentre si salva o si manda a Meta il modello non cambia: un'operazione alla volta.
  const busy = update.isPending || submit.isPending;
  const preview = previewText(body, values, stopLine);

  useLayoutEffect(() => {
    if (caret === null) return;
    const area = textRef.current;
    if (area) {
      area.focus();
      area.setSelectionRange(caret, caret);
    }
    setCaret(null);
  }, [caret]);

  /** «Inserisci nel testo»: la variabile al posto della parte scelta, o dov'è il cursore; il cursore subito dopo. */
  function insert(variable: TemplateVariable) {
    const area = textRef.current;
    if (!area || busy) return;
    const current = area.value;
    const start = area.selectionStart;
    const end = area.selectionEnd;
    const token = variableToken(variable);
    const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
    const length = sendableLength(next, stopLine);
    if (length > WHATSAPP_TEMPLATE_BODY_MAX) {
      // Non ci sta: il testo resta com'è e il campo dice perché.
      setTextError({ text: (tr) => tr("whatsappTemplatesSettings.template.tooLong", { max: WHATSAPP_TEMPLATE_BODY_MAX, count: length }) });
      area.focus();
      return;
    }
    onEdit({ body: next });
    setTextError(null);
    setCaret(start + token.length);
  }

  async function save() {
    if (busy) return;
    const nextName = name.trim();
    const nextBody = body.trim();
    setProblem(null);
    if (nextName === "") {
      setNameError({ text: (tr) => tr("whatsappTemplatesSettings.template.nameMissing") });
      nameRef.current?.focus();
      return;
    }
    if (nextBody === "") {
      setTextError({ text: (tr) => tr("whatsappTemplatesSettings.template.textMissing") });
      textRef.current?.focus();
      return;
    }
    const length = sendableLength(nextBody, stopLine);
    if (length > WHATSAPP_TEMPLATE_BODY_MAX) {
      setTextError({ text: (tr) => tr("whatsappTemplatesSettings.template.tooLong", { max: WHATSAPP_TEMPLATE_BODY_MAX, count: length }) });
      textRef.current?.focus();
      return;
    }
    try {
      const saved = await update.mutateAsync({ key: template.key, body: { name: nextName, body: nextBody, expectedVersion: edits.baseVersion ?? template.version } });
      onEdit(null);
      // Con il WhatsApp ufficiale nella pagina: un testo cambiato di un modello su Meta torna in revisione, oppure resta da mandare.
      const resubmission = meta ? saved.metaResubmission : null;
      if (resubmission?.outcome === "submitted") toast.show(t("whatsappTemplatesSettings.meta.savedInReview", { name: nextName }), "success");
      else toast.show(t("whatsappTemplatesSettings.template.saved", { name: nextName }), "success");
      if (resubmission && notResubmitted(resubmission)) {
        setProblem({ text: (tr) => tr("whatsappTemplatesSettings.meta.notResubmitted", { reason: resubmissionReason(tr, resubmission) }) });
      }
    } catch (error) {
      // Il modello si rilegge (`useUpdateWhatsAppTemplate`): le modifiche restano sopra quello salvato, che diventa la versione di
      // partenza del salvataggio dopo.
      if (error instanceof ApiRequestError && error.code === "version_conflict") {
        onEdit({ baseVersion: conflictVersion(error) ?? template.version });
        setProblem({ text: (tr) => tr("whatsappTemplatesSettings.template.conflict") });
      } else {
        setProblem({ text: (tr) => messageForError(tr, error) });
      }
    }
  }

  /** «Invia a Meta per l'approvazione»: il testo salvato; il modello com'è dopo prende il suo posto (`useSubmitWhatsAppTemplate`). */
  async function sendToMeta() {
    if (busy) return;
    setProblem(null);
    try {
      await submit.mutateAsync(template.key);
      toast.show(t("whatsappTemplatesSettings.meta.submitted"), "success");
    } catch (error) {
      // I modelli si rileggono: con un testo cambiato nel frattempo la scheda mostra quello salvato.
      if (error instanceof ApiRequestError && error.code === "version_conflict") setProblem({ text: (tr) => tr("whatsappTemplatesSettings.meta.conflict") });
      else if (error instanceof ApiRequestError && error.code === "whatsapp_meta_window_required") setProblem({ text: (tr) => tr("whatsappTemplatesSettings.meta.notConnected") });
      else setProblem({ text: (tr) => messageForError(tr, error) });
    }
  }

  const metaLine = meta ? (
    <TemplateMetaLine
      id={ids}
      template={template}
      meta={meta}
      canEdit={canEdit}
      body={body}
      bodyChanged={canEdit && canonicalTemplateBody(body.trim()) !== template.body}
      busy={busy}
      sending={submit.isPending}
      onSubmit={() => void sendToMeta()}
    />
  ) : null;

  if (!canEdit) {
    return (
      <article className="g-wa-template" aria-labelledby={`${ids}-name`}>
        <h2 id={`${ids}-name`}>{template.name}</h2>
        <p className="g-wa-muted">{suggestedText(t, template.key)}</p>
        {metaLine}
        <p className="g-wa-template__body">{template.body}</p>
        <Preview id={ids} text={preview} />
      </article>
    );
  }

  return (
    <article className="g-wa-template" aria-label={template.name}>
      <TextField
        ref={nameRef}
        id={`${ids}-name`}
        label={t("whatsappTemplatesSettings.template.name")}
        value={name}
        maxLength={WHATSAPP_TEMPLATE_NAME_MAX}
        autoComplete="off"
        error={nameError?.text(t)}
        disabled={busy}
        onChange={(event) => {
          onEdit({ name: event.target.value });
          setNameError(null);
        }}
      />
      <p className="g-wa-muted">{suggestedText(t, template.key)}</p>
      {metaLine}
      <TextArea
        ref={textRef}
        id={`${ids}-text`}
        label={t("whatsappTemplatesSettings.template.text")}
        className="g-wa-template__text"
        rows={5}
        value={body}
        maxLength={WHATSAPP_TEMPLATE_BODY_MAX}
        error={textError?.text(t)}
        disabled={busy}
        onChange={(event) => {
          onEdit({ body: event.target.value });
          setTextError(null);
        }}
      />
      <div className="g-wa-template__vars" role="group" aria-label={t("whatsappTemplatesSettings.template.insert")}>
        {TEMPLATE_VARIABLES.map((variable) => (
          <Button key={variable} variant="ghost" disabled={busy} onClick={() => insert(variable)}>
            {variableToken(variable)}
          </Button>
        ))}
      </div>
      <Preview id={ids} text={preview} />
      {problem ? (
        <Reason tone="warning" role="alert">
          {problem.text(t)}
        </Reason>
      ) : null}
      <div className="g-wa-template__foot">
        <Button variant="primary" onClick={() => void save()} disabled={busy} aria-busy={update.isPending ? "true" : undefined}>
          {t("whatsappTemplatesSettings.template.save")}
        </Button>
      </div>
    </article>
  );
}

type MetaLineProps = {
  id: string;
  template: WhatsAppTemplateView;
  meta: MetaPage;
  canEdit: boolean;
  /** Il testo nella pagina: quello che scrive chi cambia i modelli, altrimenti quello salvato. */
  body: string;
  /** Il testo nella pagina non è quello salvato: a Meta andrebbe quello salvato. */
  bodyChanged: boolean;
  /** Si salva o si manda a Meta: il pulsante è spento. */
  busy: boolean;
  /** Si manda a Meta adesso. */
  sending: boolean;
  onSubmit: () => void;
};

/**
 * Il modello presso Meta (`metaLine` del mock-up): lo stato con il suo motivo, la categoria (quella decisa da Meta, detta quando è
 * diversa da quella proposta), l'ultimo invio non riuscito e, per chi cambia i modelli, «Invia a Meta per l'approvazione» con i
 * motivi per cui è spento, uno per riga e legati al pulsante.
 */
function TemplateMetaLine({ id, template, meta, canEdit, body, bodyChanged, busy, sending, onSubmit }: MetaLineProps) {
  const t = useT();
  const status = template.metaStatus;
  const look = META_LOOK[status ?? "none"];
  const decided = template.metaCategory;
  // Già in revisione o approvato con il testo di adesso: non riparte (il server non lo manda di nuovo).
  const canSubmit = canEdit && status !== "pending" && status !== "approved";
  const atEdge = canEdit && templateVariableAtEdge(body);
  const edgeId = `${id}-meta-edge`;
  const changedId = `${id}-meta-changed`;
  // Un modello su Meta (con uno stato) torna da solo in revisione quando il testo nuovo si salva; uno mai mandato va salvato prima.
  const changedKey: MessageKey | null = bodyChanged ? (status === null ? "whatsappTemplatesSettings.meta.saveFirst" : "whatsappTemplatesSettings.meta.textChanged") : null;
  const blockers = canSubmit ? [meta.connected ? null : meta.notConnectedId, atEdge ? edgeId : null, changedKey === null ? null : changedId].filter((blocker) => blocker !== null) : [];

  return (
    <div className="g-wa-template__meta">
      <div className="g-wa-template__meta-row">
        <StatusPill tone={look.tone} icon={look.icon}>
          {t(look.label)}
        </StatusPill>
        <Chip>{t(CATEGORY_LABEL[decided ?? template.category])}</Chip>
        {canSubmit ? (
          <Button
            onClick={onSubmit}
            disabled={busy || blockers.length > 0}
            aria-busy={sending ? "true" : undefined}
            aria-describedby={blockers.length > 0 ? blockers.join(" ") : undefined}
          >
            {t("whatsappTemplatesSettings.meta.submit")}
          </Button>
        ) : null}
      </div>
      {decided !== null && decided !== template.category ? (
        <p className="g-wa-muted">
          {t("whatsappTemplatesSettings.meta.categoryChanged", { decided: t(CATEGORY_LABEL[decided]), proposed: t(CATEGORY_LABEL[template.category]) })}
        </p>
      ) : null}
      {status === "rejected" ? <Reason tone="warning">{t("whatsappTemplatesSettings.meta.rejected", { reason: rejectedReasonText(t, template.metaRejectedReason) })}</Reason> : null}
      {status === "paused" ? <Reason tone="warning">{t("whatsappTemplatesSettings.meta.paused")}</Reason> : null}
      {status === "disabled" ? <Reason tone="warning">{t("whatsappTemplatesSettings.meta.disabled")}</Reason> : null}
      {template.metaError !== null ? (
        <Reason tone="warning">{t("whatsappTemplatesSettings.meta.lastError", { reason: t(templateFailureReasonKey(template.metaError)) })}</Reason>
      ) : null}
      {atEdge ? (
        <Reason tone="warning" id={edgeId}>
          {t("errors.whatsapp_template_variable_at_edge")}
        </Reason>
      ) : null}
      {changedKey === null ? null : <Reason id={changedId}>{t(changedKey)}</Reason>}
    </div>
  );
}

/** «Come arriva a Giulia Ferri»: il testo come lo riceve il Lead dell'esempio, con gli a capo com'erano. */
function Preview({ id, text }: { id: string; text: string }) {
  const t = useT();
  return (
    <div className="g-field">
      <p id={`${id}-preview`} className="g-field__label">
        {t("whatsappTemplatesSettings.template.preview")}
      </p>
      <blockquote className="g-whatsapp__preview" aria-labelledby={`${id}-preview`}>
        {text}
      </blockquote>
    </div>
  );
}
